import { useCallback, useEffect, useRef, useState } from 'react';
import useParticipants from '../../src/hooks/useParticipants/useParticipants';
import { DatatrackDataTypes, useVideoContext } from '../main';
import { BeaconEventData, BeaconEvents } from '../types/DatatrackTypes';
import useHostDataTrack from './useHostDataTrack';
export var RecordingStatus;
(function (RecordingStatus) {
    RecordingStatus["NOT_RECORDING"] = "NOT_RECORDING";
    RecordingStatus["RECORDING"] = "RECORDING";
    RecordingStatus["PREPARING"] = "PREPARING";
    RecordingStatus["RECORDER_PAUSED"] = "RECORDER_PAUSED";
    RecordingStatus["RECORDER_RESUMED"] = "RECORDER_RESUMED";
})(RecordingStatus || (RecordingStatus = {}));
export default function useRecording(props) {
    const { callback, isHost = false } = props;
    const { room } = useVideoContext();
    const participants = useParticipants();
    const { localParticipant } = room;
    const [isRecording, setIsRecording] = useState(RecordingStatus.NOT_RECORDING);
    const lastRecordingState = useRef(RecordingStatus.NOT_RECORDING);
    const isPhantomConnected = useRef(false);
    const hostDataTrack = useHostDataTrack();
    // Used so the host knows when the Phantom has joined/left and perform the required actions
    useEffect(() => {
        if (isHost) {
            const isPhantomInThisCheck = participants.some((participant) => {
                return participant.identity.startsWith('record');
            });
            // If phantom wasn't connected but now it is, it's recording
            if (isPhantomInThisCheck && !isPhantomConnected.current && isRecording !== RecordingStatus.NOT_RECORDING) {
                setIsRecording(RecordingStatus.RECORDING);
            }
            // If phantom was connected but now it's not, it's not recording
            if (!isPhantomInThisCheck && isPhantomConnected.current) {
                setIsRecording(RecordingStatus.NOT_RECORDING);
            }
            isPhantomConnected.current = isPhantomInThisCheck;
        }
    }, [participants]);
    const onHostMessage = useCallback((message) => {
        const untypedMsg = JSON.parse(message);
        if (untypedMsg.type === DatatrackDataTypes.BeaconEvent) {
            if (untypedMsg.event === BeaconEvents.recordingStatus) {
                const recorderStatus = untypedMsg.param.recorderStatus;
                if (lastRecordingState.current !== recorderStatus) {
                    callback && callback(recorderStatus);
                    setIsRecording(recorderStatus);
                }
                lastRecordingState.current = recorderStatus;
            }
        }
    }, [lastRecordingState.current]);
    // Used for the attendees to know if the host has sent a recording message related
    useEffect(() => {
        if (hostDataTrack && !isHost) {
            hostDataTrack.on('message', onHostMessage);
        }
        return () => {
            hostDataTrack === null || hostDataTrack === void 0 ? void 0 : hostDataTrack.off('message', onHostMessage);
        };
    }, [hostDataTrack]);
    useEffect(() => {
        let interval;
        // Only send recording related messages if the user is host
        if (isHost) {
            interval = setInterval(() => {
                var _a, _b, _c, _d;
                const msgObj = new BeaconEventData(BeaconEvents.recordingStatus, { recorderStatus: isRecording });
                const payload = JSON.stringify(msgObj);
                const track = (_d = (_c = (_b = (_a = localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.dataTracks) === null || _a === void 0 ? void 0 : _a.values()) === null || _b === void 0 ? void 0 : _b.next()) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.track;
                track && track.send(payload);
            }, 2000);
        }
        return () => {
            interval && clearInterval(interval);
        };
    }, [isRecording]);
    const recordingPaused = () => {
        setIsRecording(RecordingStatus.RECORDER_PAUSED);
    };
    const preparingRecording = () => {
        if (!isPhantomConnected.current) {
            setIsRecording(RecordingStatus.PREPARING);
            return RecordingStatus.PREPARING;
        }
        else {
            setIsRecording(RecordingStatus.RECORDING);
            return RecordingStatus.RECORDER_RESUMED;
        }
    };
    const recordingResumed = () => {
        setIsRecording(RecordingStatus.RECORDER_RESUMED);
    };
    const recordingStopped = () => {
        setIsRecording(RecordingStatus.NOT_RECORDING);
    };
    return { isRecording, preparingRecording, recordingPaused, recordingResumed, recordingStopped };
}
//# sourceMappingURL=useIsRecording.js.map